import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { Button } from "../../../../components/ui/button";
import { findErrorMessage, handleHttpError } from "../../../../utils/utils";
import { useEffect } from "react";
import { useToast } from "../../../../hooks/use-toast";
import { useCreateEphemeralUserAdminHttp } from "../../../../hooks/http/admin/campaigns/create-ephemeral-user.admin.http";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "../../../../components/spinner.components";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";

const FormSchema = z.object({
  name: z.string().min(1, "Manager name is required"),
  phone: z
    .string()
    .min(10, "Phone number is required")
    .regex(
      /^\+?[1-9]\d{1,14}$/,
      "Invalid phone number format. Use format: +919876543210",
    ),
  visibleToBrandEnabled: z.boolean(),
});

export default function AddBrandManagerModalAdminPartial() {
  const { toast } = useToast();

  const { campaignId } = useParams();

  const navigate = useNavigate();
  const { execute: createEphemeralUser, fetching: creatingEphemeralUser } =
    useCreateEphemeralUserAdminHttp();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      phone: "+91",
      visibleToBrandEnabled: true,
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (!campaignId) {
      return;
    }
    const response = await createEphemeralUser({
      body: {
        name: data.name,
        phone: data.phone,
        visibleToBrandEnabled: data.visibleToBrandEnabled,
      },
      pathParams: { campaignId },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    toast({
      content: "Brand manager added successfully",
      toastType: "success",
    });
    navigate(-1);

    form.reset();
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];

      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);
  return (
    <DialogContent className="flex flex-col items-center gap-5 border-border w-full max-w-lg text-center text-primary">
      <DialogHeader className="w-full">
        <div className="flex flex-col gap-2.5">
          <p className="flex gap-1 font-semibold text-28 leading-8">
            Add new brand manager
          </p>
        </div>
      </DialogHeader>
      <div className="border-border border-b w-full" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="relative flex flex-col gap-4 w-full"
        >
          <FormInput
            form={form}
            name="name"
            label="Manager name"
            placeholder="Enter manager name"
            required
            type="text"
          />

          <FormInput
            form={form}
            name="phone"
            label="Phone"
            placeholder="Enter phone number"
            required
            type="text"
          />
          
          <div className="flex items-center gap-2 py-2">
						<Checkbox
							id="visible-to-brand-enabled"
							className="data-[state=checked]:bg-primarybuttonbg border-one data-[state=checked]:border-none rounded-full w-5 h-5"
							checked={form.watch("visibleToBrandEnabled")}
							onCheckedChange={(checked) =>
								form.setValue("visibleToBrandEnabled", checked === true)
							}
						/>
						<Label htmlFor="visible-to-brand-enabled">
							Show only shortlisted drafts
						</Label>
					</div>

          <Button
            disabled={creatingEphemeralUser}
            className="bg-primarybuttonbg w-full h-14 font-semibold text-black"
          >
            {creatingEphemeralUser ? (
              <Spinner width={20} />
            ) : (
              "Add brand manager"
            )}
          </Button>
        </form>
      </Form>
    </DialogContent>
  );
}
